// В какой оболочке исполняется веб-слой.
//
// Оболочек три: браузер (dev и прод-web), Capacitor-приложение на Android и
// Electron на планшете. Отличать Electron нужно при выборе адреса API: там
// веб-слой раздаётся локальным сервером оболочки, который проксирует `/api` на
// бэкенд (`desktop/src/webServer.ts`) — базовый URL всегда относительный `/api/v1`,
// а сам адрес бэкенда меняется через мост оболочки, а не в @capacitor/preferences.
//
// Признак — мосты из `desktop/src/preload.ts`: `contextBridge` ставит
// `window.restomarkerPrinter` и `window.restomarkerShell` ДО кода бандла.

export type ApiTargetInfo = { readonly target: string; readonly explicit: boolean };

export type SetApiTargetResult =
  { readonly ok: true; readonly target: string } | { readonly ok: false; readonly error: string };

export type WebUpdateDownloadResult =
  { readonly ok: true; readonly id: string } | { readonly ok: false; readonly error: string };

/** Обновление веб-слоя в оболочке. Контракт — как у `capacitor-updater` на Android. */
export type DesktopWebUpdate = {
  current(): Promise<string>;
  download(args: {
    url: string;
    version: string;
    checksum: string;
  }): Promise<WebUpdateDownloadResult>;
  apply(id: string): Promise<void>;
  confirm(): Promise<void>;
};

export type DesktopShellBridge = {
  getApiTarget(): Promise<ApiTargetInfo>;
  setApiTarget(raw: string): Promise<SetApiTargetResult>;
  /** Версия оболочки (T-041). Необязательна: у оболочек старше T-041 этого моста нет. */
  shellVersion?(): Promise<string>;
  /**
   * Вывести окно вперёд — из трея, из-под других окон (T-047, этап 2). Аргументов нет:
   * из рендерера в main-процесс не уходят ни адреса, ни пути. Необязателен: у оболочек
   * до этапа 2 его нет, там клик по уведомлению зовёт `window.focus()`.
   */
  focusWindow?(): Promise<void>;
  webUpdate: DesktopWebUpdate;
};

declare global {
  interface Window {
    restomarkerShell?: DesktopShellBridge;
  }
}

export function isDesktopShell(): boolean {
  return typeof window !== 'undefined' && 'restomarkerPrinter' in window;
}

/** Мост управления адресом бэкенда. `null` вне Electron-оболочки. */
export function desktopShellBridge(): DesktopShellBridge | null {
  return typeof window !== 'undefined' && window.restomarkerShell ? window.restomarkerShell : null;
}
